import { ref, computed, watch } from "vue";
import { defineStore } from "pinia";
import type { User, UserManager } from "oidc-client-ts";
import rbacModule, { type RbacAbility } from "@shipbob/rbac";
// Relative (not @/) imports: auth.store is MF-exposed as ./authStore, and its
// emitted d.ts ships to consumers where the producer's @/ alias can't resolve.
import { getUserManager } from "./user-manager";
import { fetchUserPermissions } from "./permissions";

// @shipbob/rbac is a UMD/CJS bundle whose exports object carries the factory
// under `default`; depending on the bundler's interop we receive either the
// factory itself or that namespace object. Unwrap both shapes.
const buildAbilityForUser =
  typeof rbacModule === "function"
    ? rbacModule
    : (rbacModule as unknown as { default: typeof rbacModule }).default;

/**
 * Permission ids from the OneID `Perm` claim, when the token carries them
 * (JWT-token clients, e.g. WMS/ToolBob-style hosts). oidc-client-ts surfaces
 * multi-value claims as arrays and single-value claims as plain strings.
 * Reference-token clients (like ShipBob.OneId.User.Clone) have NO Perm claim
 * client-side, those fall back to the user-management API.
 */
function claimPermissions(user: User): string[] {
  const perm = (user.profile as Record<string, unknown>).Perm;
  if (Array.isArray(perm)) {
    return perm.filter((p): p is string => typeof p === "string");
  }
  return typeof perm === "string" ? [perm] : [];
}

let eventsRegistered = false;

function getAuthService(): UserManager {
  const userManager = getUserManager();
  registerAuthEvents(userManager);
  return userManager;
}

function registerAuthEvents(userManager: UserManager) {
  if (eventsRegistered) return;

  userManager.events.addAccessTokenExpiring(async () => {
    try {
      console.log("[Auth] Token expiring, renewing...");
      await userManager.signinSilent();
    } catch (error) {
      console.warn("[Auth] Silent renew failed:", error);
    }
  });

  userManager.events.addSilentRenewError((error: unknown) => {
    console.error("[Auth] Silent renew error:", error);
  });

  eventsRegistered = true;
}

/**
 * Auth Store - Manages authentication state and operations
 *
 * Uses oidc-client-ts directly for OIDC (PKCE) auth against the ShipBob identity server.
 *
 * Usage:
 * ```typescript
 * import { useAuthStore } from '@/shared/auth/auth.store'
 * // or from MFE:
 * import { useAuthStore } from 'shipbob_network_intelligence/authStore'
 *
 * const authStore = useAuthStore()
 * console.log(authStore.isAuthenticated)
 * await authStore.login()
 * const token = await authStore.getAccessToken()
 * ```
 */
export const useAuthStore = defineStore("auth", () => {
  // State
  const user = ref<User | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const isInitialized = ref(false);

  // RBAC: authorization is initialized with authentication (host contract -
  // rbac state is part of the ./authStore expose). Loaded by loadRbac() on
  // the initialize/handleCallback paths, the router guard awaits initialize()
  // before any requiresAuth route renders, so the ability is ready alongside
  // the session. Cleared on logout/expiry by the watch below.
  const ability = ref<RbacAbility>();
  let abilityUserSub: string | undefined;

  watch(
    user,
    (currentUser) => {
      if (!currentUser || currentUser.expired) {
        ability.value = undefined;
        abilityUserSub = undefined;
      }
    },
    { flush: "sync" },
  );

  async function loadRbac(currentUser: User) {
    // Same user (token refresh, re-init), keep the existing ability.
    if (ability.value && abilityUserSub === currentUser.profile.sub) {
      return;
    }

    // Fast path: Perm claim in the token. Fallback: user-management API
    // (MerchantApp pattern, reference-token clients).
    let permissions = claimPermissions(currentUser);
    if (permissions.length === 0) {
      try {
        permissions = await fetchUserPermissions();
      } catch (err) {
        console.error("[Auth] Permissions fetch failed, ability is deny-all:", err);
        permissions = [];
      }
    }

    ability.value = buildAbilityForUser(permissions);
    abilityUserSub = currentUser.profile.sub;
  }

  function can(action: string, subject: string): boolean {
    return ability.value?.can(action, subject) ?? false;
  }

  // Getters
  const isAuthenticated = computed(() => {
    return !!user.value && !user.value.expired;
  });

  const accessToken = computed(() => {
    return user.value?.access_token || null;
  });

  const userProfile = computed(() => {
    return user.value?.profile || null;
  });

  // Actions
  async function initialize() {
    if (isInitialized.value) return;

    isLoading.value = true;
    try {
      const service = getAuthService();
      user.value = await service.getUser();
      if (user.value && !user.value.expired) {
        await loadRbac(user.value);
      }
    } catch (err: any) {
      error.value = err.message;
      console.error("[Auth] Initialization failed:", err);
    } finally {
      isInitialized.value = true;
      isLoading.value = false;
    }
  }

  async function login() {
    isLoading.value = true;
    error.value = null;
    try {
      const service = getAuthService();
      await service.removeUser();
      await service.signinRedirect();
    } catch (err: any) {
      error.value = err.message;
      console.error("[Auth] Login failed:", err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function logout() {
    isLoading.value = true;
    try {
      const service = getAuthService();
      await service.signoutRedirect();
      user.value = null;
    } catch (err: any) {
      error.value = err.message;
      console.error("[Auth] Logout failed:", err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function handleCallback() {
    isLoading.value = true;
    try {
      const service = getAuthService();
      user.value = (await service.signinCallback()) ?? (await service.getUser());
      if (user.value && !user.value.expired) {
        await loadRbac(user.value);
      }
      isInitialized.value = true;
    } catch (err: any) {
      error.value = err.message;
      console.error("[Auth] Callback failed:", err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function refreshToken() {
    try {
      const service = getAuthService();
      user.value = await service.signinSilent();
      console.log("[Auth] Token refreshed");
    } catch (err: any) {
      error.value = err.message;
      console.error("[Auth] Token refresh failed:", err);
      throw err;
    }
  }

  /**
   * Get access token for API calls
   * Used internally by axios interceptor
   */
  async function getAccessToken(): Promise<string | null> {
    // If no user, try to load from session
    if (!user.value) {
      await initialize();
    }

    // Check if token is expired
    if (user.value?.expired) {
      try {
        await refreshToken();
      } catch {
        return null;
      }
    }

    return accessToken.value;
  }

  return {
    // State
    user,
    isLoading,
    error,
    isInitialized,
    // Getters
    isAuthenticated,
    accessToken,
    userProfile,
    // RBAC
    ability,
    can,
    // Actions
    initialize,
    login,
    logout,
    handleCallback,
    refreshToken,
    getAccessToken,
  };
});
